import { HiCalendar, HiUser, HiRefresh, HiAdjustments } from 'react-icons/hi';
import CategoryMultiSelect, { SelectedCategoryChips } from './CategoryMultiSelect';
import { toInputDate, SOURCE_LABELS } from '../../utils/reportFormat';

function presetRange(key) {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);
  switch (key) {
    case 'week':
      start.setDate(today.getDate() - 6);
      break;
    case 'month':
      start.setDate(1);
      break;
    case 'lastMonth':
      start.setMonth(today.getMonth() - 1, 1);
      end.setDate(0);
      break;
    case 'year':
      start.setMonth(0, 1);
      break;
    default:
      break;
  }
  return { startDate: toInputDate(start), endDate: toInputDate(end) };
}

const PRESETS = [
  { key: 'today', label: 'اليوم' },
  { key: 'week', label: 'آخر ٧ أيام' },
  { key: 'month', label: 'هذا الشهر' },
  { key: 'lastMonth', label: 'الشهر الماضي' },
  { key: 'year', label: 'هذه السنة' },
];

export const defaultFilters = () => ({
  ...presetRange('month'),
  categoryIds: [],
  cashierId: '',
  source: 'all',
});

const inputClass =
  'w-full min-w-0 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all';

function FieldLabel({ children }) {
  return <span className="block text-xs font-semibold text-gray-500 mb-1.5">{children}</span>;
}

export default function ReportFilters({ filters, onChange, categories, cashiers, loadingOptions, refreshing, onRefresh }) {
  const set = (patch) => onChange({ ...filters, ...patch });

  const activePreset = PRESETS.find((p) => {
    const r = presetRange(p.key);
    return r.startDate === filters.startDate && r.endDate === filters.endDate;
  })?.key;

  const dirty =
    filters.categoryIds.length > 0 || filters.cashierId || filters.source !== 'all' || activePreset !== 'month';

  return (
    <div className="relative z-20 bg-white rounded-2xl shadow-sm border border-primary-100/40 p-4 md:p-5 space-y-4 animate-fadeIn">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-primary-50 text-primary-600">
            <HiAdjustments className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">تصفية التقرير</h3>
            <p className="text-xs text-gray-400">يتم تحديث النتائج تلقائياً عند تغيير أي معيار</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button
              type="button"
              onClick={() => onChange(defaultFilters())}
              className="px-3 py-2 rounded-xl text-xs font-semibold text-gray-500 hover:bg-gray-100 transition-all"
            >
              إعادة تعيين
            </button>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-primary-50 text-primary-600 text-xs font-semibold hover:bg-primary-100 transition-all active:scale-95 disabled:opacity-50"
          >
            <HiRefresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            تحديث
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => set(presetRange(p.key))}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              activePreset === p.key
                ? 'bg-primary-500 text-white shadow-md shadow-primary-500/25'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-3">
        <label className="xl:col-span-2">
          <FieldLabel>من تاريخ</FieldLabel>
          <div className="relative">
            <input
              type="date"
              value={filters.startDate}
              max={filters.endDate}
              onChange={(e) => e.target.value && set({ startDate: e.target.value })}
              className={inputClass}
            />
          </div>
        </label>
        <label className="xl:col-span-2">
          <FieldLabel>إلى تاريخ</FieldLabel>
          <input
            type="date"
            value={filters.endDate}
            min={filters.startDate}
            onChange={(e) => e.target.value && set({ endDate: e.target.value })}
            className={inputClass}
          />
        </label>
        <div className="xl:col-span-3">
          <FieldLabel>التصنيفات</FieldLabel>
          <CategoryMultiSelect
            categories={categories}
            selected={filters.categoryIds}
            onChange={(categoryIds) => set({ categoryIds })}
            loading={loadingOptions}
          />
        </div>
        <label className="xl:col-span-2">
          <FieldLabel>البائع</FieldLabel>
          <div className="relative">
            <HiUser className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-300 pointer-events-none" />
            <select
              value={filters.cashierId}
              onChange={(e) => set({ cashierId: e.target.value })}
              disabled={loadingOptions}
              className={`${inputClass} pr-9 appearance-none disabled:opacity-60`}
            >
              <option value="">كل البائعين</option>
              {cashiers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </label>
        <div className="sm:col-span-2 xl:col-span-3">
          <FieldLabel>مصدر البيع</FieldLabel>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1" role="radiogroup" aria-label="مصدر البيع">
            {Object.entries(SOURCE_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={filters.source === value}
                onClick={() => set({ source: value })}
                className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  filters.source === value
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <SelectedCategoryChips
        categories={categories}
        selected={filters.categoryIds}
        onChange={(categoryIds) => set({ categoryIds })}
      />

      <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
        <HiCalendar className="w-3.5 h-3.5" />
        التواريخ شاملة ليوم البداية ويوم النهاية بالكامل
      </div>
    </div>
  );
}
